import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import AdminUsersService from '../services/AdminUsersService';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IPaginationState from '../../types/state/IPaginationState';
import IAdminUser from '../types/users/IAdminUser';
import UploadState from '../../types/UploadState';
import IBaseState from '../../types/IBaseState';
import Report from '../types/Report';
import AdminUserProject from '../types/users/AdminUserProject';
import ImportStates, { ImportState } from '../constants/ImportStates';
import ImportProcess, {
    ImportPreviewStats,
} from '../../common/types/ImportProcess';
import ImportService from '../services/ImportService';
import { createNotifiedThunk } from '../../reducers/createAppAsyncThunk';

const NAME = 'AdminUsers';

const getUsers = createNotifiedThunk(
    `${NAME}/getUsers`,
    AdminUsersService.getUsers,
    { error: 'fe.admin.error.load-users' },
);
const createUser = createNotifiedThunk(
    `${NAME}/createUser`,
    AdminUsersService.createUser,
    { error: 'fe.admin.error.create-user' },
);
const updateUser = createNotifiedThunk(
    `${NAME}/updateUser`,
    AdminUsersService.updateUser,
    { error: 'fe.admin.error.update-user' },
);
const deleteUser = createNotifiedThunk(
    `${NAME}/deleteUser`,
    AdminUsersService.deleteUser,
    { error: 'fe.admin.error.delete-user' },
);
const batchDeleteUsers = createNotifiedThunk(
    `${NAME}/batchDeleteUsers`,
    AdminUsersService.batchDeleteUsers,
    {
        error: 'fe.admin.error.batch-delete-users',
        success: 'fe.admin.success.batch-delete-users',
    },
);
const getUser = createNotifiedThunk(
    `${NAME}/getUser`,
    AdminUsersService.getUser,
    { error: 'fe.admin.error.load-users' },
);
const getReports = createNotifiedThunk(
    `${NAME}/getReports`,
    AdminUsersService.getReports,
    { error: 'fe.admin.error.load-reports' },
);
const getJobMatchReports = createNotifiedThunk(
    `${NAME}/getJobMatchReports`,
    AdminUsersService.getJobMatchReports,
    { error: 'fe.admin.error.load-reports' },
);
const getProjects = createNotifiedThunk(
    `${NAME}/getProjects`,
    AdminUsersService.getProjects,
    { error: 'fe.admin.error.load-projects' },
);
const getManagedProjects = createNotifiedThunk(
    `${NAME}/getManagedProjects`,
    AdminUsersService.getManagedProjects,
    { error: 'fe.admin.error.load-projects' },
);
const archive = createNotifiedThunk(
    `${NAME}/archive`,
    AdminUsersService.archive,
    { error: 'fe.admin.error.archive-user' },
);
const anonymize = createNotifiedThunk(
    `${NAME}/anonymize`,
    AdminUsersService.anonymize,
    { error: 'fe.admin.error.anonymize-user' },
);
const resetAuth = createNotifiedThunk(
    `${NAME}/resetAuth`,
    AdminUsersService.resetAuth,
    { error: 'fe.admin.error.reset-user-credentials' },
);
const createJobMatch = createNotifiedThunk(
    `${NAME}/createJobMatch`,
    AdminUsersService.createJobMatch,
    { error: 'fe.admin.error.create-job-matching' },
);
const importUsers = createNotifiedThunk(
    `${NAME}/importUsers`,
    AdminUsersService.importUsers,
    { error: 'fe.admin.error.import-users' },
);
const confirmImport = createNotifiedThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
    { error: 'fe.admin.error.import-file' },
);
const cancelImport = createNotifiedThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
    { error: 'fe.admin.error.cancel-import' },
);
const getImportProgress = createNotifiedThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
    { error: 'fe.admin.error.import-users' },
);
const exportUsers = createNotifiedThunk(
    `${NAME}/exportUsers`,
    AdminUsersService.exportUsers,
    { error: 'fe.admin.error.export-users' },
);
const getImportTemplate = createNotifiedThunk(
    `${NAME}/getImportTemplate`,
    AdminUsersService.getImportTemplate,
    { error: 'fe.admin.error.fetch-template' },
);

export type UsersImportPreview = ImportPreviewStats & {
    users: IAdminUser[];
    validationErrors: unknown[];
};

export interface AdminUsersState {
    users: IPaginationState<IAdminUser[]>;
    detail: UploadState<IAdminUser | undefined>;
    reports: IBaseState<Report[]>;
    jobMatchReports: IBaseState<Report[]>;
    projects: IBaseState<AdminUserProject[]>;
    managedProjects: IBaseState<AdminUserProject[]>;
    import: {
        loading: boolean;
        preview: UsersImportPreview | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
        userId: number | null;
    };
}

const initialState: AdminUsersState = {
    users: {
        data: [],
        loading: false,
        pagination: { page: 1 },
    },
    detail: {
        data: undefined,
        loading: false,
        uploading: false,
    },
    reports: { data: [], loading: false },
    jobMatchReports: { data: [], loading: false },
    projects: { data: [], loading: false },
    managedProjects: { data: [], loading: false },
    import: {
        loading: false,
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
        userId: null,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport,
    },
    extraReducers: (builder) => {
        builder.addCase(getUsers.pending, (state: AdminUsersState) => {
            state.users.data = [];
        });
        builder.addCase(
            getUsers.fulfilled,
            (state: AdminUsersState, action) => {
                state.users.data = action.payload.data;
                const payload = action.payload as any;
                if (!payload.pagination) {
                    state.users.pagination = { page: 1 };
                } else {
                    state.users.pagination = payload.pagination;
                }
            },
        );
        builder.addCase(
            createUser.fulfilled,
            (state: AdminUsersState, action) => {
                state.users.data.push(action.payload.data as IAdminUser);
            },
        );
        builder.addCase(
            updateUser.fulfilled,
            (state: AdminUsersState, action) => {
                const id = action.meta.arg.userId;
                const index = state.users.data.findIndex(
                    (item) => item.id === id,
                );

                if (index >= 0) {
                    state.users.data[index] = {
                        ...state.users.data[index],
                        ...action.meta.arg,
                    };
                }
            },
        );
        builder.addCase(
            deleteUser.fulfilled,
            (state: AdminUsersState, action) => {
                const id = action.meta.arg;
                const index = state.users.data.findIndex(
                    (item) => item.id === id,
                );

                if (index >= 0) {
                    state.users.data.splice(index, 1);
                }
            },
        );
        builder.addCase(
            batchDeleteUsers.fulfilled,
            (state: AdminUsersState, action) => {
                const deletedIds = action.meta.arg.ids;
                state.users.data = state.users.data.filter(
                    (user) => !deletedIds.includes(user.id),
                );
            },
        );

        builder.addCase(getUser.pending, (state: AdminUsersState) => {
            state.detail.loading = true;
            state.detail.data = undefined;
        });
        builder.addCase(getUser.fulfilled, (state: AdminUsersState, action) => {
            state.detail.data = action.payload.data;
        });

        builder.addCase(getReports.pending, (state: AdminUsersState) => {
            state.reports.loading = true;
            state.reports.data = [];
        });
        builder.addCase(
            getReports.fulfilled,
            (state: AdminUsersState, action) => {
                state.reports.data = action.payload.data;
            },
        );
        builder.addCase(getReports.rejected, (state: AdminUsersState) => {
            state.reports.data = [];
        });
        builder.addCase(
            getJobMatchReports.pending,
            (state: AdminUsersState) => {
                state.jobMatchReports.loading = true;
                state.jobMatchReports.data = [];
            },
        );
        builder.addCase(
            getJobMatchReports.fulfilled,
            (state: AdminUsersState, action) => {
                state.jobMatchReports.data = action.payload.data;
            },
        );
        builder.addCase(
            getJobMatchReports.rejected,
            (state: AdminUsersState) => {
                state.jobMatchReports.data = [];
            },
        );

        builder.addCase(getProjects.pending, (state: AdminUsersState) => {
            state.projects.loading = true;
            state.projects.data = [];
        });
        builder.addCase(
            getProjects.fulfilled,
            (state: AdminUsersState, action) => {
                state.projects.data = action.payload.data;
            },
        );
        builder.addCase(getProjects.rejected, (state: AdminUsersState) => {
            state.projects.data = [];
        });
        builder.addCase(
            getManagedProjects.pending,
            (state: AdminUsersState) => {
                state.managedProjects.loading = true;
                state.managedProjects.data = [];
            },
        );
        builder.addCase(
            getManagedProjects.fulfilled,
            (state: AdminUsersState, action) => {
                state.managedProjects.data = action.payload.data;
            },
        );
        builder.addCase(
            getManagedProjects.rejected,
            (state: AdminUsersState) => {
                state.managedProjects.data = [];
            },
        );

        builder.addCase(importUsers.pending, (state: AdminUsersState) => {
            resetImport(state);
            state.import.state = ImportStates.Pending;
        });
        builder.addCase(
            importUsers.fulfilled,
            (state: AdminUsersState, action) => {
                const data = action.payload.data as {
                    importId: number;
                    preview: unknown;
                };
                state.import.state = ImportStates.Preview;
                state.import.importId = data.importId;
                state.import.preview = data.preview as UsersImportPreview;
            },
        );
        builder.addCase(importUsers.rejected, (state: AdminUsersState) => {
            resetImport(state);
            state.import.state = ImportStates.Fail;
        });
        builder.addCase(confirmImport.pending, (state: AdminUsersState) => {
            state.import.state = ImportStates.Pending;
        });
        builder.addCase(
            confirmImport.fulfilled,
            (state: AdminUsersState, action) => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data as ImportProcess;
                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(confirmImport.rejected, (state: AdminUsersState) => {
            resetImport(state);
        });
        builder.addCase(cancelImport.pending, (state: AdminUsersState) => {
            state.import.state = ImportStates.Idle;
            state.import.importId = null;
        });
        builder.addCase(
            getImportProgress.fulfilled,
            (state: AdminUsersState, action) => {
                const data = action.payload.data as ImportProcess;

                state.import.process = data;
                if (data?.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: AdminUsersState) => {
                resetImport(state);
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(
                getUsers.pending,
                createUser.pending,
                updateUser.pending,
                deleteUser.pending,
                batchDeleteUsers.pending,
            ),
            (state: AdminUsersState) => {
                state.users.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getUsers.fulfilled,
                createUser.fulfilled,
                updateUser.fulfilled,
                deleteUser.fulfilled,
                batchDeleteUsers.fulfilled,
                getUsers.rejected,
                createUser.rejected,
                updateUser.rejected,
                deleteUser.rejected,
                batchDeleteUsers.rejected,
            ),
            (state: AdminUsersState) => {
                state.users.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getUser.fulfilled, getUser.rejected),
            (state: AdminUsersState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getUser.pending, getUser.rejected),
            (state: AdminUsersState) => {
                state.detail.data = undefined;
            },
        );

        builder.addMatcher(
            isAnyOf(getReports.fulfilled, getReports.rejected),
            (state: AdminUsersState) => {
                state.reports.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getJobMatchReports.fulfilled, getJobMatchReports.rejected),
            (state: AdminUsersState) => {
                state.jobMatchReports.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getProjects.fulfilled, getProjects.rejected),
            (state: AdminUsersState) => {
                state.projects.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getManagedProjects.fulfilled, getManagedProjects.rejected),
            (state: AdminUsersState) => {
                state.managedProjects.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getImportProgress.rejected, importUsers.rejected),
            (state: AdminUsersState) => {
                state.import.state = ImportStates.Idle;
            },
        );
    },
});

const state = (state: AppState) => state.admin.users;

const AdminUsersSlice = {
    reducer: slice.reducer,
    actions: {
        getUsers,
        createUser,
        updateUser,
        deleteUser,
        batchDeleteUsers,
        getUser,
        getReports,
        getJobMatchReports,
        getProjects,
        getManagedProjects,
        archive,
        anonymize,
        resetAuth,
        createJobMatch,
        importUsers,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportUsers,
        getImportTemplate,
        ...slice.actions,
    },
    state: {
        all: state,
        list: createSelector(state, (state: AdminUsersState) => state.users),
        detail: createSelector(state, (state: AdminUsersState) => state.detail),
        reports: createSelector(
            state,
            (state: AdminUsersState) => state.reports,
        ),
        jobMatchReports: createSelector(
            state,
            (state: AdminUsersState) => state.jobMatchReports,
        ),
        projects: createSelector(
            state,
            (state: AdminUsersState) => state.projects,
        ),
        managedProjects: createSelector(
            state,
            (state: AdminUsersState) => state.managedProjects,
        ),
        import: createSelector(state, (state: AdminUsersState) => state.import),
    },
};

function resetImport(state: AdminUsersState) {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
}

export default AdminUsersSlice;
